/**
 * Session ID management for analytics
 * Uses sessionStorage to persist session ID within tab session
 * Matches iOS behavior where session ID is generated on app launch
 */

const SESSION_ID_KEY = 'analytics_session_id'

/**
 * Generate a new UUID session ID
 */
function generateSessionId(): string {
  // Use crypto.randomUUID() if available, otherwise fallback to simple UUID
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get or create session ID from sessionStorage
 * Returns existing session ID if available, otherwise generates new one
 */
export function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY)
    if (!sessionId) {
      sessionId = generateSessionId()
      sessionStorage.setItem(SESSION_ID_KEY, sessionId)
    }
    return sessionId
  } catch (error) {
    // Fallback if sessionStorage is not available
    console.warn('sessionStorage not available, using in-memory session ID')
    return generateSessionId()
  }
}

/**
 * Clear session ID from sessionStorage
 * Should be called on logout
 */
export function clearSessionId(): void {
  try {
    sessionStorage.removeItem(SESSION_ID_KEY)
  } catch (error) {
    // Ignore errors
  }
}
